import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.MODE !== 'production') {
      console.error('[ErrorBoundary]', error, info);
    }
  }

  private reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <main
        role="alert"
        className="min-h-screen flex items-center justify-center p-6"
      >
        <div className="max-w-md space-y-4 text-center">
          <h1 className="text-2xl font-semibold text-foreground">
            Algo deu errado
          </h1>
          <p className="text-base text-muted-foreground">
            Não conseguimos carregar esta tela. Tente novamente ou volte para o
            início.
          </p>
          {import.meta.env.MODE !== 'production' && (
            <pre className="text-left text-xs text-muted-foreground bg-muted rounded-md p-3 overflow-auto max-h-40">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button onClick={this.reset} autoFocus>
              Tentar novamente
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                window.location.assign('/');
              }}
            >
              Voltar ao início
            </Button>
          </div>
        </div>
      </main>
    );
  }
}
